
import { useState, useEffect } from "react";
import { User } from "../types";
import { supabase } from "@/integrations/supabase/client";

export const useAuthState = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [authError, setAuthError] = useState<string | null>(null);

  // Fetch user profile from the user_profiles table
  const fetchProfile = async (userId: string, userEmail: string) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching profile:', error.message);
        return;
      }
      
      if (data) {
        // Check if the fields exist in the data object before accessing them
        setUser({
          id: userId,
          email: userEmail,
          full_name: data.first_name || '',
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          company: data.company || '',
          phone: '', // Phone field removed for security
          role: data.role || '',
          preferred_language: data.preferred_language || 'sv',
        });
      } else {
        // Fallback if profile doesn't exist yet
        setUser({
          id: userId,
          email: userEmail,
          full_name: '',
          first_name: '',
          last_name: '',
          company: '',
          phone: '',
          role: '',
        });
      }
    } catch (error: any) {
      console.error('Error in fetchProfile:', error.message);
    }
  };

  // Initialize auth state and listen for changes
  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);
      
      try {
        // Check for existing session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error getting session:", error.message);
          return;
        }
        
        if (session?.user) {
          await fetchProfile(session.user.id, session.user.email);
        }
        
        // Set up auth state change listener
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            if (session?.user) {
              await fetchProfile(session.user.id, session.user.email);
            } else {
              setUser(null);
            }
          }
        );
        
        // Clean up subscription on unmount
        return () => {
          subscription.unsubscribe();
        };
      } catch (err: any) {
        console.error("Auth initialization error:", err.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeAuth();
  }, []);

  return { user, setUser, isLoading, setIsLoading, authError, setAuthError, fetchProfile };
};
